import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';

import useWindowSize from './useWindowSize';

export const errorMessage1 = 'You must either add a `ref` to the element you are interacting with or pass an `event` to openPortal(e) or togglePortal(e).';

const usePortal = ({ portalStyles, closeOnOutsideClick, onClose }) => {
  const [isOpen, makeOpen] = useState(false);

  const open = useRef(isOpen);
  const targetEl = useRef();
  const portal = useRef(document.createElement('div'));
  const eventListeners = useRef({});

  const elToMountTo = useMemo(() => document.body, []);

  const windowSize = useWindowSize();

  const setOpen = useCallback((v) => {
    open.current = v;
    makeOpen(v);
  }, []);

  useEffect(
    () => {
      if (!portal.current) portal.current = document.createElement('div');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [portal],
  );

  const createCustomEvent = (e) => {
    if (!e) return { portal, targetEl, event: e };
    const event = e || {};
    if (event.persist) event.persist();
    event.portal = portal;
    event.targetEl = targetEl;
    event.event = e;
    const { currentTarget } = e;
    if (!targetEl.current && currentTarget && currentTarget !== document) targetEl.current = event.currentTarget;
    return event;
  };

  const openPortal = useCallback(
    (e) => {
      createCustomEvent(e);
      // for some reason, when we don't have the event argument, there
      // is a weird race condition. Would like to see if we can remove
      // setTimeout, but for now this works
      if (targetEl.current == null) {
        setTimeout(() => setOpen(true), 0);
        throw Error(errorMessage1);
      }

      // const targetRect = targetEl.current.getBoundingClientRect();
      if (portalStyles) {
        const mobile = windowSize && windowSize?.width < 370;
        portal.current.style.cssText = portalStyles(mobile, windowSize);
      }
      if (windowSize?.width < 370) {
        document.body.style.overflow = 'hidden';
      }
      setOpen(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [portal, setOpen, targetEl, windowSize],
  );

  const closePortal = useCallback(
    (e) => {
      createCustomEvent(e);
      if (open.current) setOpen(false);
      onClose?.();
      portal.current.removeAttribute('style');
      if (windowSize?.width < 370) {
        document.body.style.overflow = 'auto';
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setOpen],
  );

  const togglePortal = useCallback(
    (e) => (open.current ? closePortal(e) : openPortal(e)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [closePortal, openPortal],
  );

  const handleKeydown = useCallback(
    (e) => (e.key === 'Escape' && closeOnOutsideClick ? closePortal(e) : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [closePortal],
  );

  const handleOutsideMouseClick = useCallback(
    (e) => {
      const containsTarget = (target) => target.current.contains(e.target);
      if (containsTarget(portal) || e.button !== 0 || !open.current || containsTarget(targetEl)) return;
      if (closeOnOutsideClick) closePortal(e);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [closePortal, portal],
  );

  const handleMouseDown = useCallback(
    (e) => {
      createCustomEvent(e);
      if (!(portal.current instanceof HTMLElement)) return;
      handleOutsideMouseClick(e);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleOutsideMouseClick],
  );

  useEffect(
    () => {
      if (!(elToMountTo instanceof HTMLElement) || !(portal.current instanceof HTMLElement)) return;

      const eventHandlerMap = {
        onScroll: 'scroll',
        onWheel: 'wheel',
      };
      const node = portal.current;
      elToMountTo.appendChild(portal.current);
      document.addEventListener('keydown', handleKeydown);
      document.addEventListener('mousedown', handleMouseDown);

      return () => {
        Object.entries(eventHandlerMap).forEach(([handlerName, eventListenerName]) => {
          document.removeEventListener(eventListenerName, eventListeners.current[handlerName]);
          // eslint-disable-next-line react-hooks/exhaustive-deps
          delete eventListeners.current[handlerName];
        });
        document.removeEventListener('keydown', handleKeydown);
        document.removeEventListener('mousedown', handleMouseDown);
        elToMountTo.removeChild(node);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleOutsideMouseClick, handleKeydown, elToMountTo, portal],
  );

  const Portal = useCallback(
    ({ children }) => {
      if (portal.current != null) return createPortal(children, portal.current);
      return null;
    },
    [portal],
  );

  return Object.assign([openPortal, closePortal, open.current, Portal, togglePortal, targetEl, portal], {
    isOpen: open.current,
    openPortal,
    ref: targetEl,
    closePortal,
    togglePortal,
    Portal,
    portalRef: portal,
    bind: {
      ref: targetEl,
    },
  });
};

export default usePortal;
